<template>
  <div class="player-demographics-analysis">
    <!-- Navigation Header -->
    <div class="analytics-nav-header">
      <div class="breadcrumb">
        <button class="breadcrumb-item" @click="navigateBack">
          <i class="bi bi-speedometer2"></i>
          Analytics Dashboard
        </button>
        <i class="bi bi-chevron-right breadcrumb-separator"></i>
        <span class="breadcrumb-current">Player Demographics Analysis</span>
      </div>
      <button class="back-button" @click="navigateBack">
        <i class="bi bi-arrow-left"></i>
        Back to Dashboard
      </button>
    </div>

    <div class="section-header">
      <h3>Player Demographics Analysis</h3>
      <p>
        Comprehensive breakdown of assessed player demographics and conversion
        metrics
      </p>
    </div>

    <!-- Summary Cards -->
    <div class="demographics-summary-cards">
      <div class="summary-card">
        <div class="summary-icon primary">
          <i class="bi bi-people"></i>
        </div>
        <div class="summary-content">
          <h4>{{ totalPlayersAssessed }}</h4>
          <p>Total Players Assessed</p>
          <span class="summary-trend trend-positive">
            +{{ assessmentTrend }}% this period
          </span>
        </div>
      </div>

      <div class="summary-card">
        <div class="summary-icon success">
          <i class="bi bi-geo-alt"></i>
        </div>
        <div class="summary-content">
          <h4>{{ activeRegions }}</h4>
          <p>Active Scouting Regions</p>
          <span class="summary-detail">
            {{ topRegion }} is most productive
          </span>
        </div>
      </div>

      <div class="summary-card">
        <div class="summary-icon warning">
          <i class="bi bi-diagram-3"></i>
        </div>
        <div class="summary-content">
          <h4>{{ conversionRate }}%</h4>
          <p>Assessed → Trialist Rate</p>
          <span class="summary-detail">
            {{ conversionTrend }} vs target 15%
          </span>
        </div>
      </div>

      <div class="summary-card">
        <div class="summary-icon info">
          <i class="bi bi-calendar-range"></i>
        </div>
        <div class="summary-content">
          <h4>{{ averageAge }}</h4>
          <p>Average Player Age</p>
          <span class="summary-detail">
            Peak assessments: {{ peakAgeRange }}
          </span>
        </div>
      </div>
    </div>

    <div class="analytics-grid">
      <!-- Age Distribution Analysis -->
      <div class="chart-container">
        <div class="chart-header">
          <h4>Age Distribution Analysis</h4>
          <p class="chart-subtitle">Player assessments by age group</p>
        </div>
        <div class="chart-placeholder">
          <canvas ref="ageDistributionChart" width="400" height="300"></canvas>
        </div>
      </div>

      <!-- Geographic Heat Map -->
      <div class="chart-container">
        <div class="chart-header">
          <h4>Geographic Talent Sources</h4>
          <p class="chart-subtitle">
            Regional distribution of assessed players
          </p>
        </div>
        <div class="geographic-heatmap">
          <div class="region-item" v-for="region in geographicData" :key="region.name">
            <div class="region-header">
              <span class="region-name">{{ region.name }}</span>
              <span class="region-count">{{ region.playerCount }} players</span>
            </div>
            <div class="region-bar">
              <div class="region-fill" :style="{ width: region.percentage + '%' }"
                :class="getRegionClass(region.productivity)"></div>
            </div>
            <div class="region-details">
              <span>{{ region.conversionRate }}% conversion</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Position Scarcity Analysis -->
      <div class="chart-container">
        <div class="chart-header">
          <h4>Position Scarcity Analysis</h4>
          <p class="chart-subtitle">
            Assessment frequency vs conversion by position
          </p>
        </div>
        <div class="chart-placeholder">
          <canvas ref="positionScarcityChart" width="400" height="300"></canvas>
        </div>
      </div>

      <!-- Competition Level Breakdown -->
      <div class="chart-container">
        <div class="chart-header">
          <h4>Competition Level Breakdown</h4>
          <p class="chart-subtitle">Player sources by competition tier</p>
        </div>
        <div class="chart-placeholder">
          <canvas ref="competitionChart" width="400" height="300"></canvas>
        </div>
      </div>

      <!-- Conversion Funnel -->
      <div class="chart-container full-width">
        <div class="chart-header">
          <h4>Conversion Funnel Analytics</h4>
          <p class="chart-subtitle">Journey from assessment to signed player</p>
        </div>
        <div class="funnel-visualization">
          <div class="funnel-stage" v-for="stage in funnelData" :key="stage.name">
            <div class="funnel-bar" :style="{ width: stage.percentage + '%' }" :class="'funnel-' + stage.color">
              <div class="funnel-content">
                <span class="funnel-number">{{ stage.count }}</span>
                <span class="funnel-label">{{ stage.name }}</span>
              </div>
            </div>
            <div class="funnel-metrics">
              <span class="conversion-rate">{{ stage.conversionRate }}% conversion</span>
              <span class="dropout-rate">{{ stage.dropoutRate }}% dropout</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Detailed Tables -->
    <div class="details-section">
      <div class="detail-table-container">
        <div class="detail-header">
          <h4>Regional Performance Breakdown</h4>
        </div>
        <div class="table-responsive">
          <table class="demographics-table">
            <thead>
              <tr>
                <th>Region</th>
                <th>Players Assessed</th>
                <th>Conversion Rate</th>
                <th>Top Position</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="region in detailedRegionalData" :key="region.name">
                <td class="region-name-cell">
                  <strong>{{ region.name }}</strong>
                  <small>{{ region.towns.join(", ") }}</small>
                </td>
                <td class="number-cell">{{ region.assessed }}</td>
                <td class="percentage-cell" :class="getPerformanceClass(region.conversionRate)">
                  {{ region.conversionRate }}%
                </td>
                <td class="position-cell">{{ region.topPosition }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js/auto";

export default {
  name: "PlayerDemographicsAnalysis",
  props: {
    analyticsData: {
      type: Object,
      default: () => ({}),
    },
    dateRange: {
      type: Array,
      default: () => [],
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    // Assessment data for real calculations
    allAssessmentsData: {
      type: Array,
      default: () => [],
    },
    signedTrialistsData: {
      type: Array,
      default: () => [],
    },
    gameAttendanceData: {
      type: Array,
      default: () => [],
    },
    playerLeadsData: {
      type: Array,
      default: () => [],
    },
    trialistsData: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["data-point-click", "navigate-back"],
  data() {
    return {
      ageDistributionChart: null,
      positionScarcityChart: null,
      competitionChart: null,

      // Summary metrics - calculated from real assessment data
      totalPlayersAssessed: 0,
      assessmentTrend: 0,
      activeRegions: 0,
      topRegion: "N/A",
      conversionRate: 0,
      conversionTrend: "0%",
      averageAge: 0,
      peakAgeRange: "N/A",

      // Geographic data - calculated from real assessment data
      geographicData: [],

      // Funnel data - calculated from real assessment data
      funnelData: [],

      // Detailed regional data - calculated from real assessment data
      detailedRegionalData: [],
    };
  },
  watch: {
    // Recalculate metrics when assessment data changes
    allAssessmentsData() {
      this.calculateDemographicsMetrics();
      this.refreshAgeDistributionChart();
      this.refreshPositionScarcityChart();
    },
    signedTrialistsData() {
      this.calculateDemographicsMetrics();
      this.refreshPositionScarcityChart();
    },
    gameAttendanceData() {
      this.calculateDemographicsMetrics();
      this.refreshAgeDistributionChart();
    },
    playerLeadsData() {
      this.calculateDemographicsMetrics();
    },
    trialistsData() {
      this.calculateDemographicsMetrics();
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.calculateDemographicsMetrics(); // Calculate real metrics first
      this.createAgeDistributionChart();
      this.createPositionScarcityChart();
      this.createCompetitionChart();
    });
  },
  beforeUnmount() {
    // Halt animations before destroy so the animator doesn't tick into a
    // null canvas after the DOM node is gone (Chart.js clearCanvas crash).
    if (this.ageDistributionChart) {
      try { this.ageDistributionChart.stop(); } catch (e) { /* noop */ }
      this.ageDistributionChart.destroy();
      this.ageDistributionChart = null;
    }
    if (this.positionScarcityChart) {
      try { this.positionScarcityChart.stop(); } catch (e) { /* noop */ }
      this.positionScarcityChart.destroy();
      this.positionScarcityChart = null;
    }
    if (this.competitionChart) {
      try { this.competitionChart.stop(); } catch (e) { /* noop */ }
      this.competitionChart.destroy();
      this.competitionChart = null;
    }
  },
  methods: {
    // Calculate demographics metrics from real assessment data
    calculateDemographicsMetrics() {
      if (!this.allAssessmentsData.length) {
        // Set default values when no data available
        this.totalPlayersAssessed = 0;
        this.assessmentTrend = 0;
        this.activeRegions = 0;
        this.topRegion = "N/A";
        this.conversionRate = 0;
        this.conversionTrend = "0%";
        this.averageAge = 0;
        this.peakAgeRange = "N/A";
        return;
      }

      // Total players assessed
      this.totalPlayersAssessed = this.allAssessmentsData.length;

      // Calculate assessment trend (current period vs previous)
      this.assessmentTrend = this.calculateAssessmentTrend();

      // Calculate regional data
      const regionData = this.calculateRegionalData();
      this.activeRegions = regionData.count;
      this.topRegion = regionData.topRegion;

      // Calculate conversion rate (assessments to signings)
      const signedCount = this.signedTrialistsData.length;
      this.conversionRate =
        this.totalPlayersAssessed > 0
          ? ((signedCount / this.totalPlayersAssessed) * 100).toFixed(1)
          : 0;

      // Calculate conversion trend
      this.conversionTrend = this.calculateConversionTrend();

      // Calculate age metrics
      const ageData = this.calculateAgeMetrics();
      this.averageAge = ageData.average;
      this.peakAgeRange = ageData.peakRange;

      // Calculate geographic data
      this.calculateGeographicData();

      // Calculate funnel data
      this.calculateFunnelData();

      // Calculate detailed regional data
      this.calculateDetailedRegionalData();
    },

    // Calculate assessment trend over time
    calculateAssessmentTrend() {
      if (!this.gameAttendanceData.length) return 0;

      const currentDate = new Date();
      const threeMonthsAgo = new Date(
        currentDate.getTime() - 90 * 24 * 60 * 60 * 1000,
      );
      const sixMonthsAgo = new Date(
        currentDate.getTime() - 180 * 24 * 60 * 60 * 1000,
      );

      // Recent assessments (last 3 months)
      const recentGames = this.gameAttendanceData.filter(
        (game) => new Date(game.matchDate) >= threeMonthsAgo,
      );
      const recentAssessments = this.allAssessmentsData.filter((assessment) =>
        recentGames.some(
          (game) => game.id === assessment.scoutAttendanceReportId,
        ),
      ).length;

      // Previous assessments (3-6 months ago)
      const previousGames = this.gameAttendanceData.filter((game) => {
        const gameDate = new Date(game.matchDate);
        return gameDate >= sixMonthsAgo && gameDate < threeMonthsAgo;
      });
      const previousAssessments = this.allAssessmentsData.filter((assessment) =>
        previousGames.some(
          (game) => game.id === assessment.scoutAttendanceReportId,
        ),
      ).length;

      if (previousAssessments === 0) return recentAssessments > 0 ? 100 : 0;
      return (
        ((recentAssessments - previousAssessments) / previousAssessments) *
        100
      ).toFixed(1);
    },

    // Calculate regional data from scout reports
    calculateRegionalData() {
      if (!this.gameAttendanceData.length) {
        return { count: 0, topRegion: "N/A" };
      }

      // Count unique regions/locations from game attendance data
      const regions = new Set();
      const regionCounts = {};

      this.gameAttendanceData.forEach((game) => {
        if (game.location || game.venue) {
          const region = game.location || game.venue;
          regions.add(region);
          regionCounts[region] = (regionCounts[region] || 0) + 1;
        }
      });

      // Find most productive region
      let topRegion = "N/A";
      let maxCount = 0;
      Object.keys(regionCounts).forEach((region) => {
        if (regionCounts[region] > maxCount) {
          maxCount = regionCounts[region];
          topRegion = region;
        }
      });

      return {
        count: regions.size,
        topRegion: topRegion,
      };
    },

    // Calculate conversion trend
    calculateConversionTrend() {
      if (!this.gameAttendanceData.length || !this.allAssessmentsData.length)
        return "0%";

      const currentDate = new Date();
      const threeMonthsAgo = new Date(
        currentDate.getTime() - 90 * 24 * 60 * 60 * 1000,
      );
      const sixMonthsAgo = new Date(
        currentDate.getTime() - 180 * 24 * 60 * 60 * 1000,
      );

      // Recent conversion rate
      const recentGames = this.gameAttendanceData.filter(
        (game) => new Date(game.matchDate) >= threeMonthsAgo,
      );
      const recentAssessments = this.allAssessmentsData.filter((assessment) =>
        recentGames.some(
          (game) => game.id === assessment.scoutAttendanceReportId,
        ),
      ).length;
      const recentSignings = this.signedTrialistsData.filter(
        (trialist) =>
          new Date(trialist.signedDate || trialist.created_date) >=
          threeMonthsAgo,
      ).length;
      const recentConversion =
        recentAssessments > 0 ? (recentSignings / recentAssessments) * 100 : 0;

      // Previous conversion rate
      const previousGames = this.gameAttendanceData.filter((game) => {
        const gameDate = new Date(game.matchDate);
        return gameDate >= sixMonthsAgo && gameDate < threeMonthsAgo;
      });
      const previousAssessments = this.allAssessmentsData.filter((assessment) =>
        previousGames.some(
          (game) => game.id === assessment.scoutAttendanceReportId,
        ),
      ).length;
      const previousSignings = this.signedTrialistsData.filter((trialist) => {
        const signedDate = new Date(
          trialist.signedDate || trialist.created_date,
        );
        return signedDate >= sixMonthsAgo && signedDate < threeMonthsAgo;
      }).length;
      const previousConversion =
        previousAssessments > 0
          ? (previousSignings / previousAssessments) * 100
          : 0;

      // Calculate trend
      if (previousConversion === 0)
        return recentConversion > 0 ? "+100%" : "0%";
      const trend =
        ((recentConversion - previousConversion) / previousConversion) * 100;
      return (trend >= 0 ? "+" : "") + trend.toFixed(1) + "%";
    },

    // Calculate age metrics from assessment data. Assessments don't carry
    // a DOB column — the player's age cohort lives on the parent
    // game_attendance_report row as `age_group` (a year-of-birth string,
    // e.g. "2017"). Resolve via scoutAttendanceReportId.
    calculateAgeMetrics() {
      if (!this.allAssessmentsData.length) {
        return { average: 0, peakRange: "N/A" };
      }

      const reportsById = {};
      (this.gameAttendanceData || []).forEach((g) => {
        reportsById[g.id] = g;
      });

      const currentYear = new Date().getFullYear();
      const ages = [];
      const ageCounts = {};

      this.allAssessmentsData.forEach((assessment) => {
        const report = reportsById[assessment.scoutAttendanceReportId];
        const birthYearStr = report && report.age_group;
        if (!birthYearStr) return;
        const birthYear = parseInt(birthYearStr, 10);
        if (isNaN(birthYear) || birthYear < 1990 || birthYear > currentYear) return;

        const age = currentYear - birthYear;
        if (age > 0 && age < 30) {
          ages.push(age);
          ageCounts[age] = (ageCounts[age] || 0) + 1;
        }
      });

      if (ages.length === 0) {
        return { average: 0, peakRange: "N/A" };
      }

      // Calculate average age
      const averageAge = (
        ages.reduce((sum, age) => sum + age, 0) / ages.length
      ).toFixed(1);

      // Find peak age range (most common ages)
      let maxCount = 0;
      let peakAge = 0;
      Object.keys(ageCounts).forEach((age) => {
        if (ageCounts[age] > maxCount) {
          maxCount = ageCounts[age];
          peakAge = parseInt(age);
        }
      });

      // Create age range around peak
      const peakRange = peakAge > 0 ? `${peakAge - 1}-${peakAge + 1}` : "N/A";

      return {
        average: averageAge,
        peakRange: peakRange,
      };
    },

    // Calculate geographic data from real assessment data
    calculateGeographicData() {
      if (!this.gameAttendanceData.length || !this.allAssessmentsData.length) {
        this.geographicData = [];
        return;
      }

      // Group assessments by game location/venue
      const regionStats = {};

      this.gameAttendanceData.forEach((game) => {
        const region = game.location || game.venue || "Unknown";
        if (!regionStats[region]) {
          regionStats[region] = {
            name: region,
            playerCount: 0,
            assessments: [],
            signings: 0,
          };
        }

        // Count assessments for this game/region
        const gameAssessments = this.allAssessmentsData.filter(
          (assessment) => assessment.scoutAttendanceReportId === game.id,
        );
        regionStats[region].playerCount += gameAssessments.length;
        regionStats[region].assessments.push(...gameAssessments);
      });

      // Count signings per region
      Object.keys(regionStats).forEach((region) => {
        const regionAssessments = regionStats[region].assessments;
        regionStats[region].signings = this.signedTrialistsData.filter(
          (trialist) =>
            regionAssessments.some(
              (assessment) =>
                assessment.playerId === trialist.playerId ||
                assessment.playerId === trialist.player_id ||
                assessment.playerId === trialist.id,
            ),
        ).length;
      });

      // Convert to array and calculate metrics
      const regions = Object.values(regionStats)
        .filter((region) => region.playerCount > 0)
        .sort((a, b) => b.playerCount - a.playerCount);

      const maxCount = regions.length > 0 ? regions[0].playerCount : 1;

      this.geographicData = regions.map((region) => {
        const conversionRate =
          region.playerCount > 0
            ? ((region.signings / region.playerCount) * 100).toFixed(1)
            : 0;

        let productivity = "low";
        if (conversionRate >= 10) productivity = "high";
        else if (conversionRate >= 7) productivity = "medium";

        return {
          name: region.name,
          playerCount: region.playerCount,
          percentage: Math.round((region.playerCount / maxCount) * 100),
          productivity: productivity,
          conversionRate: parseFloat(conversionRate),
        };
      });
    },

    // Calculate funnel data from real data only. The four stages map to
    // tables that actually exist:
    //   Watched at game  -> count of players_being_assessed rows
    //   Lead created     -> count of player_leads rows
    //   Trialist invited -> count of trialists table rows (active + signed + archived)
    //   Signed           -> count of signed trialists
    // The previous version invented stages 2–5 with hardcoded percentages.
    calculateFunnelData() {
      const totalAssessments = (this.allAssessmentsData || []).length;
      const totalLeads = (this.playerLeadsData || []).length;
      const totalTrialists =
        (this.trialistsData || []).length +
        (this.signedTrialistsData || []).length;
      const totalSignings = (this.signedTrialistsData || []).length;

      if (totalAssessments + totalLeads + totalTrialists + totalSignings === 0) {
        this.funnelData = [];
        return;
      }

      const stages = [
        {
          name: "Watched at a game",
          count: totalAssessments,
          color: "blue",
        },
        { name: "Lead created", count: totalLeads, color: "green" },
        {
          name: "Trialist invited",
          count: totalTrialists,
          color: "orange",
        },
        { name: "Signed", count: totalSignings, color: "success" },
      ];

      // Calculate percentages and rates
      this.funnelData = stages.map((stage, index) => {
        const percentage =
          totalAssessments > 0
            ? Math.round((stage.count / totalAssessments) * 100)
            : 0;

        let conversionRate = 100;
        let dropoutRate = 0;

        if (index > 0) {
          const previousStage = stages[index - 1];
          conversionRate =
            previousStage.count > 0
              ? ((stage.count / previousStage.count) * 100).toFixed(1)
              : 0;
          dropoutRate = (100 - conversionRate).toFixed(1);
        }

        return {
          name: stage.name,
          count: stage.count,
          percentage: percentage,
          color: stage.color,
          conversionRate: parseFloat(conversionRate),
          dropoutRate: parseFloat(dropoutRate),
        };
      });
    },

    // Calculate detailed regional data for the table
    calculateDetailedRegionalData() {
      if (!this.gameAttendanceData.length || !this.allAssessmentsData.length) {
        this.detailedRegionalData = [];
        return;
      }

      // Group data by region and calculate detailed metrics
      const regionStats = {};

      this.gameAttendanceData.forEach((game) => {
        const region = game.location || game.venue || "Unknown";
        if (!regionStats[region]) {
          regionStats[region] = {
            name: region,
            assessments: [],
            signings: 0,
            positions: {},
            towns: new Set(),
          };
        }

        // Add town/location info
        if (game.location) regionStats[region].towns.add(game.location);
        if (game.venue) regionStats[region].towns.add(game.venue);

        // Collect assessments for this game/region
        const gameAssessments = this.allAssessmentsData.filter(
          (assessment) => assessment.scoutAttendanceReportId === game.id,
        );
        regionStats[region].assessments.push(...gameAssessments);

        // Count positions
        gameAssessments.forEach((assessment) => {
          if (assessment.position) {
            const position = assessment.position;
            regionStats[region].positions[position] =
              (regionStats[region].positions[position] || 0) + 1;
          }
        });
      });

      // Calculate signings per region
      Object.keys(regionStats).forEach((region) => {
        const regionAssessments = regionStats[region].assessments;
        regionStats[region].signings = this.signedTrialistsData.filter(
          (trialist) =>
            regionAssessments.some(
              (assessment) =>
                assessment.playerId === trialist.playerId ||
                assessment.playerId === trialist.player_id ||
                assessment.playerId === trialist.id,
            ),
        ).length;
      });

      // Convert to detailed table format
      this.detailedRegionalData = Object.values(regionStats)
        .filter((region) => region.assessments.length > 0)
        .sort((a, b) => b.assessments.length - a.assessments.length)
        .map((region) => {
          const assessed = region.assessments.length;
          const conversionRate =
            assessed > 0 ? ((region.signings / assessed) * 100).toFixed(1) : 0;

          // Find top position
          let topPosition = "N/A";
          let maxPositionCount = 0;
          Object.keys(region.positions).forEach((position) => {
            if (region.positions[position] > maxPositionCount) {
              maxPositionCount = region.positions[position];
              topPosition = position;
            }
          });

          return {
            name: region.name,
            towns: Array.from(region.towns).slice(0, 4), // Limit to 4 towns for display
            assessed: assessed,
            conversionRate: parseFloat(conversionRate),
            topPosition: topPosition,
          };
        });
    },

    // Bucket assessments by player age. Each assessment joins to a
    // game_attendance_report via scoutAttendanceReportId; that report's
    // `age_group` column holds the player's birth year (e.g. "2008").
    // We derive current age and bucket into <16, 16, 17, 18, 19, 20, 21+.
    computeAgeDistribution() {
      const labels = ["<U16", "U16", "U17", "U18", "U19", "U20", "U21+"];
      const data = [0, 0, 0, 0, 0, 0, 0];

      const reportsById = {};
      (this.gameAttendanceData || []).forEach((g) => {
        reportsById[g.id] = g;
      });

      const currentYear = new Date().getFullYear();
      (this.allAssessmentsData || []).forEach((a) => {
        const report = reportsById[a.scoutAttendanceReportId];
        if (!report || !report.age_group) return;
        const birthYear = parseInt(report.age_group, 10);
        if (isNaN(birthYear)) return;
        const age = currentYear - birthYear;
        if (age < 16) data[0]++;
        else if (age === 16) data[1]++;
        else if (age === 17) data[2]++;
        else if (age === 18) data[3]++;
        else if (age === 19) data[4]++;
        else if (age === 20) data[5]++;
        else data[6]++;
      });

      return { labels, data };
    },

    refreshAgeDistributionChart() {
      if (!this.ageDistributionChart) return;
      const buckets = this.computeAgeDistribution();
      this.ageDistributionChart.data.labels = buckets.labels;
      this.ageDistributionChart.data.datasets[0].data = buckets.data;
      this.ageDistributionChart.update("none");
    },

    createAgeDistributionChart() {
      const ctx = this.$refs.ageDistributionChart?.getContext("2d");
      if (!ctx) return;

      const ageBuckets = this.computeAgeDistribution();

      this.ageDistributionChart = new Chart(ctx, {
        type: "bar",
        data: {
          labels: ageBuckets.labels,
          datasets: [
            {
              label: "Players Assessed",
              data: ageBuckets.data,
              backgroundColor: "rgba(54, 162, 235, 0.6)",
              borderColor: "rgba(54, 162, 235, 1)",
              borderWidth: 1,
              borderRadius: 4,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: "Age Distribution of Assessed Players",
              font: { size: 14, weight: "bold" },
            },
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: "Number of Players",
              },
            },
            x: {
              title: {
                display: true,
                text: "Age Group",
              },
            },
          },
        },
      });
    },

    // Build a position-scarcity scatter from real data: count assessments
    // per position bucket, and compute conversion as the % of those
    // assessments whose name+team appears in signedTrialistsData.
    computePositionScarcity() {
      const positionGroups = {
        Goalkeeper: ["Goalkeeper"],
        Defender: [
          "Center-back left",
          "Center-back right",
          "Right Full-back (Wingback)",
          "Left Full-back (Wingback)",
        ],
        Midfielder: [
          "Defensive Midfielder",
          "Centre midfield",
          "Centre Defensive Midfield",
          "Attacking Midfielder",
          "Right Midfielder (Winger)",
          "Left Midfielder (Winger)",
        ],
        Forward: [
          "Center Forward (Striker) left",
          "Center Forward (Striker) right",
        ],
      };
      const positionToBucket = {};
      Object.entries(positionGroups).forEach(([bucket, list]) => {
        list.forEach((p) => {
          positionToBucket[p] = bucket;
        });
      });

      const signedNames = new Set(
        (this.signedTrialistsData || []).map((p) =>
          (p.name || "").trim().toLowerCase(),
        ),
      );

      const stats = {};
      Object.keys(positionGroups).forEach((b) => {
        stats[b] = { assessed: 0, signed: 0 };
      });

      (this.allAssessmentsData || []).forEach((a) => {
        const bucket = positionToBucket[a.position];
        if (!bucket) return;
        stats[bucket].assessed++;
        if (signedNames.has((a.name || "").trim().toLowerCase())) {
          stats[bucket].signed++;
        }
      });

      return Object.entries(stats)
        .filter(([, s]) => s.assessed > 0)
        .map(([position, s]) => ({
          x: s.assessed,
          y: s.assessed > 0 ? Number(((s.signed / s.assessed) * 100).toFixed(1)) : 0,
          position,
        }));
    },

    refreshPositionScarcityChart() {
      if (!this.positionScarcityChart) return;
      this.positionScarcityChart.data.datasets[0].data =
        this.computePositionScarcity();
      this.positionScarcityChart.update("none");
    },

    createPositionScarcityChart() {
      const ctx = this.$refs.positionScarcityChart?.getContext("2d");
      if (!ctx) return;

      this.positionScarcityChart = new Chart(ctx, {
        type: "scatter",
        data: {
          datasets: [
            {
              label: "Position Analysis",
              data: this.computePositionScarcity(),
              backgroundColor: "rgba(255, 99, 132, 0.6)",
              borderColor: "rgba(255, 99, 132, 1)",
              borderWidth: 2,
              pointRadius: 8,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: "Position Scarcity Analysis",
              font: { size: 14, weight: "bold" },
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const point = context.raw;
                  return `${point.position}: ${point.x} assessed, ${point.y}% conversion`;
                },
              },
            },
          },
          scales: {
            x: {
              title: {
                display: true,
                text: "Players Assessed",
              },
            },
            y: {
              title: {
                display: true,
                text: "Conversion Rate (%)",
              },
            },
          },
        },
      });
    },

    createCompetitionChart() {
      const ctx = this.$refs.competitionChart?.getContext("2d");
      if (!ctx) return;

      this.competitionChart = new Chart(ctx, {
        type: "doughnut",
        data: {
          labels: [
            "Academy League",
            "School Football",
            "Amateur League",
            "Semi-Pro",
            "Youth Cup",
          ],
          datasets: [
            {
              data: [45, 25, 15, 10, 5],
              backgroundColor: [
                "rgba(54, 162, 235, 0.6)",
                "rgba(255, 99, 132, 0.6)",
                "rgba(255, 205, 86, 0.6)",
                "rgba(75, 192, 192, 0.6)",
                "rgba(153, 102, 255, 0.6)",
              ],
              borderWidth: 2,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: "Competition Level Distribution",
              font: { size: 14, weight: "bold" },
            },
            legend: {
              position: "bottom",
            },
          },
        },
      });
    },

    getRegionClass(productivity) {
      switch (productivity) {
        case "high":
          return "high-productivity";
        case "medium":
          return "medium-productivity";
        case "low":
          return "low-productivity";
        default:
          return "";
      }
    },

    getPerformanceClass(value) {
      if (value >= 10) return "excellent";
      if (value >= 7) return "good";
      if (value >= 5) return "Progressing well";
      return "Unsatisfactory";
    },

    navigateBack() {
      this.$emit("navigate-back");
    },
  },
};
</script>

<style scoped>
.player-demographics-analysis {
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.section-header {
  text-align: center;
  margin-bottom: 2rem;
}

.section-header h3 {
  color: #2c3e50;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.section-header p {
  color: #6c757d;
  margin: 0;
}

/* Summary Cards */
.demographics-summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.summary-card {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  gap: 1rem;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.summary-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  flex-shrink: 0;
}

.summary-icon.primary {
  background: linear-gradient(135deg, #007bff, #6610f2);
}

.summary-icon.success {
  background: linear-gradient(135deg, #28a745, #20c997);
}

.summary-icon.warning {
  background: linear-gradient(135deg, #ffc107, #fd7e14);
}

.summary-icon.info {
  background: linear-gradient(135deg, #17a2b8, #6f42c1);
}

.summary-content h4 {
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0 0 0.25rem 0;
  color: #2c3e50;
}

.summary-content p {
  margin: 0 0 0.5rem 0;
  color: #495057;
  font-weight: 500;
}

.summary-trend {
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  display: inline-block;
}

.summary-trend.trend-positive {
  background: #d4edda;
  color: #155724;
}

.summary-detail {
  font-size: 0.85rem;
  color: #6c757d;
  font-weight: 500;
}

/* Analytics Grid */
.analytics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
}

.chart-container {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.chart-container.full-width {
  grid-column: 1 / -1;
}

.chart-header {
  margin-bottom: 1.5rem;
}

.chart-header h4 {
  color: #2c3e50;
  margin: 0;
  font-weight: 600;
}

.chart-subtitle {
  color: #6c757d;
  font-size: 0.9rem;
  margin: 0.25rem 0 0 0;
}

.chart-placeholder {
  position: relative;
  height: 300px;
  border-radius: 8px;
  overflow: hidden;
}

/* Geographic Heatmap */
.geographic-heatmap {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.region-item {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #dee2e6;
  transition: border-color 0.2s ease;
}

.region-item:hover {
  border-left-color: #007bff;
}

.region-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.region-name {
  font-weight: 600;
  color: #2c3e50;
}

.region-count {
  font-weight: 700;
  color: #495057;
}

.region-bar {
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.region-fill {
  height: 100%;
  transition: width 0.3s ease;
  border-radius: 4px;
}

.region-fill.high-productivity {
  background: linear-gradient(90deg, #28a745, #20c997);
}

.region-fill.medium-productivity {
  background: linear-gradient(90deg, #ffc107, #fd7e14);
}

.region-fill.low-productivity {
  background: linear-gradient(90deg, #dc3545, #e83e8c);
}

.region-details {
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  color: #6c757d;
}

.distance {
  font-style: italic;
}

/* Funnel Visualization */
.funnel-visualization {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.funnel-stage {
  position: relative;
}

.funnel-bar {
  height: 60px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  padding: 0 1rem;
  transition: width 0.3s ease;
  min-width: 200px;
}

.funnel-blue {
  background: linear-gradient(90deg, #007bff, #6610f2);
}

.funnel-green {
  background: linear-gradient(90deg, #28a745, #20c997);
}

.funnel-orange {
  background: linear-gradient(90deg, #ffc107, #fd7e14);
}

.funnel-purple {
  background: linear-gradient(90deg, #6f42c1, #e83e8c);
}

.funnel-red {
  background: linear-gradient(90deg, #dc3545, #fd7e14);
}

.funnel-success {
  background: linear-gradient(90deg, #28a745, #17a2b8);
}

.funnel-content {
  display: flex;
  gap: 1rem;
  align-items: center;
  color: white;
  font-weight: 600;
}

.funnel-number {
  font-size: 1.2rem;
  font-weight: 700;
}

.funnel-metrics {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  gap: 1rem;
  font-size: 0.85rem;
  color: #6c757d;
}

/* Details Section */
.details-section {
  margin-top: 2rem;
}

.detail-table-container {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.detail-header h4 {
  color: #2c3e50;
  margin: 0;
  font-weight: 600;
}

.detail-controls .form-select {
  min-width: 150px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  font-size: 0.9rem;
}

.table-responsive {
  overflow-x: auto;
}

.demographics-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
}

.demographics-table th,
.demographics-table td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #dee2e6;
}

.demographics-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #2c3e50;
}

.region-name-cell strong {
  display: block;
  color: #2c3e50;
}

.region-name-cell small {
  color: #6c757d;
  font-size: 0.8rem;
}

.number-cell,
.percentage-cell,
.distance-cell,
.currency-cell {
  font-weight: 600;
}

.percentage-cell.excellent {
  color: #28a745;
}

.percentage-cell.good {
  color: #17a2b8;
}

.percentage-cell.average {
  color: #ffc107;
}

.percentage-cell.poor {
  color: #dc3545;
}

.position-cell {
  font-style: italic;
  color: #6c757d;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .player-demographics-analysis {
    padding: 1rem;
  }

  .demographics-summary-cards {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .analytics-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .detail-header {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .funnel-metrics {
    position: static;
    transform: none;
    margin-top: 0.5rem;
    justify-content: flex-start;
  }

  .analytics-nav-header {
    flex-direction: column;
    gap: 1rem;
  }

  .breadcrumb {
    justify-content: center;
  }
}

/* Navigation Header Styles */
.analytics-nav-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  margin-bottom: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  border-left: 4px solid #007bff;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.9rem;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #007bff;
  cursor: pointer;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  transition: all 0.2s ease;
  font-weight: 500;
}

.breadcrumb-item:hover {
  background: #e3f2fd;
  color: #0056b3;
}

.breadcrumb-item i {
  font-size: 1rem;
}

.breadcrumb-separator {
  color: #6c757d;
  font-size: 0.75rem;
}

.breadcrumb-current {
  color: #495057;
  font-weight: 600;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.2);
}

.back-button:hover {
  background: #0056b3;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
}

.back-button i {
  font-size: 1rem;
}
</style>
